<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :is-loading="status.loading"
    :loading-message="status.loadingMessage"
    button-location="top"
    @submit="loadWillCallRides"
  >
    <template #title>
      Will Call Rides
    </template>

    <template #top-form>
      <form
        class="form-horizontal"
        autocomplete="off"
        @submit.prevent="loadWillCallRides"
      >
        <fieldset>
          <div class="row align-items-center">
            <label class="col-sm-3 control-label"> Phone Number* </label>
            <phone-number-input
              v-model="filters.phoneNumber"
              class="col-sm-6"
              :page-id="pageId"
            />
          </div>

          <div class="row">
            <label class="col-sm-3 control-label pt-10">Pickup date</label>
            <div class="col-sm-6">
              <date-picker
                v-model="pickupDate"
                type="date"
              />
            </div>
          </div>

          <div class="row align-items-center">
            <label class="col-sm-3 control-label">Status</label>
            <div class="col-sm-8 filter-button-group d-flex align-items-center flex-wrap">
              <el-checkbox
                :model-value="isAllStatusChecked"
                :indeterminate="isAllStatusIndeterminate"
                class="mr-15"
                @change="onStatusAllChange"
              >
                All
              </el-checkbox>
              <el-checkbox
                v-for="opt in statusOptions"
                :key="opt"
                :model-value="filters.status.includes(opt)"
                class="mr-15"
                @change="(checked: boolean) => toggleStatus(opt, checked)"
              >
                {{ statusLabel(opt) }}
              </el-checkbox>
            </div>
          </div>

          <div class="form-submit-button-group">
            <p-button
              type="purple"
              round
              native-type="submit"
              :disabled="status.loading || !canSubmit"
            >
              Search
            </p-button>

            <p-button
              type="yellow"
              round
              :disabled="status.loading"
              @click="resetForm"
            >
              Reset
            </p-button>
          </div>
        </fieldset>
      </form>

      <fieldset class="mt-20 mobile-mt-20">
        <pagination
          class="mb-10"
          :current-page="paginationInfo.currentPage"
          :total-pages="paginationInfo.totalPages"
          @load-page-data="loadPage"
        />
      </fieldset>

      <message-html :result-html="status.resultHtml" />
    </template>

    <template #content>
      <table-view-container
        class="pt-10 pb-10 pl-10 pr-10"
        :page-id="pageId"
        :data="rows"
        :table-columns="tableColumns"
        :fixed-column-counts="2"
      >
        <template #right-fixed-columns>
          <el-table-column
            fixed="right"
            label="Action"
            width="50"
          >
            <template #default="scope">
              <div
                v-if="canRemove(scope.row)"
                class="info-icon"
                @click="onCancelWillCallRide(scope.row)"
              >
                <i class="fa fa-times" />
              </div>
            </template>
          </el-table-column>
        </template>
      </table-view-container>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from 'vue';
import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { useDashboard } from '@/use/app';
import { useCustomer } from '@/use/customer';
import { scheduledRideService } from '@/services/scheduledRide';
import {
  type WillCallRidesQueryVariables,
  WillCallRideStatusFilter,
} from '@/gql/graphql';
import { TableColumnType } from '@/models/table';

const statusOptions = Object.values(WillCallRideStatusFilter);

function statusLabel(opt: WillCallRideStatusFilter) {
  return opt.charAt(0).toUpperCase() + opt.slice(1);
}

const pageId = 'willcall-rides';
const paginationLimit = 20;

const tableColumns: TableColumnType[] = [
  { label: 'ID', field: 'id', width: 80 },
  { label: 'Caller', field: 'callerName', width: 140 },
  { label: 'Phone', field: 'callerPhoneNumber', width: 120 },
  { label: 'Partner ID', field: 'partnerId', width: 100 },
  { label: 'Pickup date', field: 'pickupDate', width: 180, isDateField: true },
  { label: 'Pickup', field: 'streetAddress1', width: 200 },
  { label: 'Dropoff', field: 'streetAddress2', width: 200 },
  { label: 'Executed At', field: 'executedAt', width: 180, isDateField: true },
  { label: 'Completed At', field: 'completedAt', width: 180, isDateField: true },
  { label: 'Canceled At', field: 'canceledAt', width: 180, isDateField: true },
  { label: 'Created', field: 'createdAt', width: 180, isDateField: true },
];

const { isPartnerDashboard } = useDashboard();
const { customerPhoneNumber, customerInfo } = useCustomer();
const { showConfirmPopupDialogWithValidation } = useSwalDialog();

const cancelReasonDefault = computed(() =>
  isPartnerDashboard.value ? 'Removed by partner' : 'Removed by operator',
);

const rows = ref<Record<string, unknown>[]>([]);
const status = reactive({
  loading: false,
  loadingMessage: 'Loading Will Call rides...',
  searched: false,
  resultHtml: '',
});

const pickupDate = ref<Date | null>(null);

watch(pickupDate, (value: Date | null) => {
  filters.pickupDate = value ? util.formatDateTime(value, 'YYYY-MM-DD', customerInfo.value?.timeZone) : '';
});

const filters = reactive<{
  phoneNumber: string;
  status: WillCallRideStatusFilter[];
  pickupDate: string;
}>({
  phoneNumber: '',
  status: [WillCallRideStatusFilter.Active],
  pickupDate: '',
});

const isAllStatusChecked = computed(() => filters.status.length === statusOptions.length);
const isAllStatusIndeterminate = computed(() => filters.status.length > 0 && filters.status.length < statusOptions.length);

function toggleStatus(opt: WillCallRideStatusFilter, checked: boolean) {
  if (checked && !filters.status.includes(opt)) {
    filters.status = [...filters.status, opt];
  } else if (!checked) {
    filters.status = filters.status.filter((s) => s !== opt);
  }
}

function onStatusAllChange(checked: boolean) {
  filters.status = checked ? [...statusOptions] : [];
}

const paginationInfo = reactive({
  currentPage: 1,
  totalPages: 1,
});

// Sync phone from right panel when caller is loaded
watch(
  customerPhoneNumber,
  (phone) => {
    if (phone && !filters.phoneNumber) {
      filters.phoneNumber = phone;
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (customerPhoneNumber.value) {
    filters.phoneNumber = customerPhoneNumber.value;
  }

  loadWillCallRides();
});

/** Allow submit when no phone entered, or when entered phone is valid */
const canSubmit = computed(
  () =>
    !filters.phoneNumber.trim() ||
    Boolean(util.getValidPhoneNumber(filters.phoneNumber)),
);

function canRemove(row: Record<string, unknown>) {
  return !row.canceledAt && !row.completedAt && !row.executedAt;
}

async function onCancelWillCallRide(row: Record<string, unknown>) {
  const id = row.id as number;
  const cancellationReason = await showConfirmPopupDialogWithValidation({
    title: 'Cancel Will Call ride?',
    text: `Cancel Will Call ride #${id}?`,
    input: 'text',
    inputPlaceholder: 'Please input cancellation reason',
    inputValue: cancelReasonDefault.value,
    validationText: 'Please input cancellation reason',
    confirmButtonText: 'Yes, Cancel',
  });

  if (!cancellationReason) {
    return;
  }

  try {
    await scheduledRideService.cancelWillCallRide({
      willCallRideId: id,
      cancellationReason,
    });
    status.resultHtml = util.setSuccessMessage('Pre-approved ride canceled.');
    await loadPage(paginationInfo.currentPage);
  } catch (error) {
    status.resultHtml = util.getGraphqlError(error);
  }
}

async function loadPage(currentPage: number = 1) {
  paginationInfo.currentPage = currentPage;
  status.resultHtml = '';
  status.searched = true;
  status.loading = true;

  const validPhone = filters.phoneNumber
    ? util.getValidPhoneNumber(filters.phoneNumber.toString())
    : undefined;

  try {
    const query: WillCallRidesQueryVariables = {
      pagination: {
        limit: paginationLimit,
        offset: (currentPage - 1) * paginationLimit,
      },
      status: filters.status,
    };

    if (validPhone) {
      query.phoneNumber = validPhone;
    }

    if (filters.pickupDate) {
      query.pickupDate = filters.pickupDate;
    }

    const response = await scheduledRideService.fetchWillCallRideRecords(query);

    paginationInfo.totalPages = Math.ceil((response?.count ?? 0) / paginationLimit) || 1;
    rows.value = (response?.rows ?? []).map((r: Record<string, unknown>) => ({ ...r }));
  } catch (error) {
    paginationInfo.totalPages = 1;
    rows.value = [];
    status.resultHtml = util.getGraphqlError(error);
  } finally {
    status.loading = false;
  }
}

async function loadWillCallRides() {
  if (!canSubmit.value) return;
  await loadPage(1);
}

async function resetForm() {
  filters.phoneNumber = '';
  filters.status = [WillCallRideStatusFilter.Active];
  filters.pickupDate = '';
  pickupDate.value = null;
  paginationInfo.currentPage = 1;
  paginationInfo.totalPages = 1;
  status.searched = false;
  status.resultHtml = '';
  await loadPage(1);
}
</script>
